/**
 * Hero block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS,
 * so the theme ships both together.
 *
 * Hover and keyboard focus open the hotspot tooltips in CSS alone, so the block
 * is fully usable with this script blocked. What JS adds is the part CSS cannot
 * express: Escape must dismiss an open tooltip without moving focus
 * (WCAG 1.4.13), and a touch device has no hover to open one with.
 *
 * The scroll parallax is not here: src/js/motion.js feeds every hero marked
 * `data-scroll-hero` its numbers from the one scroll loop the site runs.
 */

import './index.scss';

const heroes = document.querySelectorAll('.hero');

if (heroes.length) {
    const OPEN = 'data-dismissed';

    /**
     * Re-arm every tooltip in a hero after a dismissal.
     *
     * @param {Element} hero Hero section root.
     * @returns {void}
     */
    const clearDismissed = (hero) => {
        for (const spot of hero.querySelectorAll(`[${OPEN}]`)) {
            spot.removeAttribute(OPEN);
        }
    };

    for (const hero of heroes) {
        const hotspots = hero.querySelectorAll('.hero__hotspot');

        if (!hotspots.length) {
            continue;
        }

        for (const hotspot of hotspots) {
            const trigger = hotspot.querySelector('.hero__hotspot-trigger');

            if (!trigger) {
                continue;
            }

            // Pointer or keyboard re-entry means the visitor wants it back.
            hotspot.addEventListener('pointerenter', () => hotspot.removeAttribute(OPEN));
            trigger.addEventListener('focus', () => hotspot.removeAttribute(OPEN));

            // A button trigger has no navigation to perform, so on touch it
            // doubles as the open/close control.
            if ('BUTTON' === trigger.tagName) {
                trigger.addEventListener('click', () => {
                    const dismissed = 'true' === hotspot.getAttribute(OPEN);
                    clearDismissed(hero);

                    if (!dismissed) {
                        hotspot.setAttribute(OPEN, 'true');
                    }
                });
            }
        }

        hero.addEventListener('keydown', (event) => {
            if ('Escape' !== event.key) {
                return;
            }

            // Focus beats hover: Escape is a keyboard action, and a single
            // selector would hand back whichever dot came first in the
            // document — dismissing the wrong tooltip whenever the pointer
            // happens to rest on one dot while another holds focus.
            const open =
                hero.querySelector('.hero__hotspot:focus-within') ||
                hero.querySelector('.hero__hotspot:hover');

            if (open) {
                // Dismiss only — focus deliberately stays where it is.
                open.setAttribute(OPEN, 'true');
            }
        });
    }
}
